import React, { useState, useEffect } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Switch,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { useAuth } from '../../App'
import { useTranslation } from 'react-i18next'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

interface UserBilling {
  id: number
  email: string
  role: string
  stripe_customer_id: string | null
  auto_invoice_enabled: boolean
  invoice_day_of_month: number
  last_auto_invoice_date: string | null
  billing_email: string | null
  billing_company: string | null
  billing_address: object | null
  billing_vat_id: string | null
}

const BillingTab: React.FC = () => {
  const { t } = useTranslation()
  const auth = useAuth()
  const [users, setUsers] = useState<UserBilling[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingUserId, setEditingUserId] = useState<number | null>(null)
  const [editValues, setEditValues] = useState<{
    auto_invoice_enabled: boolean
    invoice_day_of_month: number
  }>({ auto_invoice_enabled: false, invoice_day_of_month: 1 })
  const [saving, setSaving] = useState(false)

  const fetchUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/admin/billing/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }
      const data = await response.json()
      setUsers(data)
    } catch (err) {
      setError('Failed to load billing users')
      console.error('Fetch users error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleEditClick = (user: UserBilling) => {
    setEditingUserId(user.id)
    setEditValues({
      auto_invoice_enabled: user.auto_invoice_enabled,
      invoice_day_of_month: user.invoice_day_of_month || 1
    })
  }

  const handleCancelEdit = () => {
    setEditingUserId(null)
  }

  const handleSave = async (userId: number) => {
    setSaving(true)
    setError(null)
    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/admin/billing/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editValues)
      })
      if (!response.ok) {
        throw new Error('Failed to update billing settings')
      }
      await fetchUsers()
      setEditingUserId(null)
    } catch (err) {
      setError('Failed to save billing settings')
      console.error('Save billing error:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('userBillingSettings')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('billingSettingsDescription')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('user')}</TableCell>
              <TableCell>{t('email')}</TableCell>
              <TableCell>{t('stripeCustomer')}</TableCell>
              <TableCell align="center">{t('autoInvoicing')}</TableCell>
              <TableCell align="center">{t('invoiceDay')}</TableCell>
              <TableCell>{t('lastInvoice')}</TableCell>
              <TableCell align="center">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{user.id}</Typography>
                    {user.role === 'admin' && (
                      <Chip label="Admin" size="small" color="primary" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.stripe_customer_id ? (
                    <Chip label={user.stripe_customer_id.slice(0, 15) + '...'} size="small" />
                  ) : (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {editingUserId === user.id ? (
                    <Switch
                      checked={editValues.auto_invoice_enabled}
                      onChange={(e) => setEditValues(prev => ({
                        ...prev,
                        auto_invoice_enabled: e.target.checked
                      }))}
                      disabled={saving}
                    />
                  ) : (
                    <Switch checked={user.auto_invoice_enabled} disabled />
                  )}
                </TableCell>
                <TableCell align="center">
                  {editingUserId === user.id ? (
                    <TextField
                      type="number"
                      size="small"
                      value={editValues.invoice_day_of_month}
                      onChange={(e) => setEditValues(prev => ({
                        ...prev,
                        invoice_day_of_month: Math.min(28, Math.max(1, parseInt(e.target.value) || 1))
                      }))}
                      inputProps={{ min: 1, max: 28, style: { width: '50px' } }}
                      disabled={saving}
                    />
                  ) : (
                    <Typography variant="body2">
                      {user.invoice_day_of_month || '-'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {user.last_auto_invoice_date ? (
                    new Date(user.last_auto_invoice_date).toLocaleDateString()
                  ) : (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {editingUserId === user.id ? (
                    <Box display="flex" gap={1} justifyContent="center">
                      <Tooltip title={t('save')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSave(user.id)}
                          disabled={saving}
                        >
                          {saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('cancel')}>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={saving}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title={t('edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default BillingTab
